import { useState, useMemo, useEffect, useCallback } from "react";
import { BriefcaseIcon } from "@heroicons/react/solid";
import { BriefcaseIcon as BriefcaseIconOutline, UserCircleIcon } from "@heroicons/react/outline";

import { ManageRolesDialog } from "../../Workspaces/WorkspaceMembersTable/ManageRolesDialog";
import type { NormalizedMemberData as WorkspaceMemberData } from "../../Workspaces/WorkspaceMembersTable/hooks/utils";
import {
  NormalizedMemberData,
  NormalizedGroupedInvite,
  isOrganizationMember,
  isWorkspaceInvite,
} from "../hooks/utils";

import { useMemberDetailActions } from "./hooks/useMemberDetailActions";
import { useWorkspacesTable, type WorkspaceTableRow } from "./hooks/useWorkspacesTable";
import { useInviteTable, type InviteTableRow } from "./hooks/useInviteTable";
import { UserInfoSection } from "./UserInfoSection";

import { DataTable } from "@/components/ui/ag-grid-table";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { TableColumn } from "@/components/ui/TableV2";
import TabNavigation from "@/components/ui/TabNavigation";
import { Workspace } from "@/types/workspaces";
import { WorkspaceMember } from "@/types/workspaceMembers";
import { useOrganization, useOrganizationRoles } from "@/queries/organizations";
import { useGetWorkspaceMembers, useGetWorkspaces } from "@/queries";
import { useCurrentUser } from "@/queries/users";
import { useGetWorkspaceInvites } from "@/queries/workspace";
import { useIsOrgOwnerById } from "@/hooks/organizations";
import { useDebounce } from "@/hooks/debounce";
import { useIsNarrowWidth } from "@/hooks/useIsNarrowWidth";
import { useIsShortHeight } from "@/hooks/useIsShortHeight";
import type { OrganizationMembersTableWorkspaceInfo } from "@/types/organization-member";
import type { OrganizationRole } from "@/types/organization-roles";

interface MemberDetailSheetProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  member: NormalizedMemberData | null;
  organizationId: number;
  onMemberDelete?: () => void;
  onInviteDelete?: () => void;
  onInviteCreate?: () => void;
}

export const MemberDetailSheet = ({
  open,
  onOpenChange,
  member,
  organizationId,
  onMemberDelete,
  onInviteDelete,
  onInviteCreate,
}: MemberDetailSheetProps) => {
  const [selectedWorkspace, setSelectedWorkspace] = useState<Workspace | null>(null);
  const [isManageRolesOpen, setIsManageRolesOpen] = useState(false);
  const [isInviteDialogOpen, setIsInviteDialogOpen] = useState(false);
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(20);
  const debouncedSearch = useDebounce(search, 300);

  const { data: workspacesData, isLoading: isLoadingWorkspaces } = useGetWorkspaces({
    organizationId,
    search: debouncedSearch || undefined,
    page,
    perPage,
  });
  const { data: workspaceMembersData } = useGetWorkspaceMembers(
    selectedWorkspace?.id.toString() ?? "",
    isManageRolesOpen && !!selectedWorkspace,
  );
  const isOrgOwner = useIsOrgOwnerById(organizationId);
  const { data: organization } = useOrganization(organizationId);
  const isRBACEnabled = organization?.is_rbac_enabled;
  const { data: organizationRoles } = useOrganizationRoles();
  const { data: currentUser } = useCurrentUser();

  const orgMember = member && isOrganizationMember(member) ? member : null;
  const [removedWorkspaceNames, setRemovedWorkspaceNames] = useState<Set<string>>(() => new Set());
  const [organizationRoleOverride, setOrganizationRoleOverride] = useState<OrganizationRole | null>(
    null,
  );
  const orgMemberForDisplay = useMemo(() => {
    if (!orgMember) {
      return null;
    }
    if (removedWorkspaceNames.size === 0 && !organizationRoleOverride) {
      return orgMember;
    }
    const nextWorkspaces =
      removedWorkspaceNames.size === 0
        ? orgMember.workspaces
        : orgMember.workspaces.filter(w => !removedWorkspaceNames.has(w.name));
    const withWorkspaces = {
      ...orgMember,
      workspaces: nextWorkspaces,
    };
    if (organizationRoleOverride) {
      const isOrgOwnerRole = organizationRoleOverride.name.toLowerCase() === "owner";
      // Org owners are workspace admins; mirror that in UI until the list row refetches.
      const roleAdjustedWorkspaces = isOrgOwnerRole
        ? withWorkspaces.workspaces.map((ws: OrganizationMembersTableWorkspaceInfo) => ({
            ...ws,
            is_admin: true,
            roles: Array.isArray(ws.roles)
              ? ws.roles.filter((r: string) => r.toLowerCase() !== "admin")
              : ws.roles,
          }))
        : withWorkspaces.workspaces;
      return {
        ...withWorkspaces,
        organization_role: organizationRoleOverride,
        is_owner: isOrgOwnerRole,
        workspaces: roleAdjustedWorkspaces,
      };
    }
    return withWorkspaces;
  }, [orgMember, removedWorkspaceNames, organizationRoleOverride]);
  const groupedInvite: NormalizedGroupedInvite | null =
    member && isWorkspaceInvite(member) ? member : null;
  const detailInviteEmail = orgMember?.user.email ?? groupedInvite?.invitee_email;
  const { data: detailInvitesData, refetch: refetchDetailInvites } = useGetWorkspaceInvites(
    detailInviteEmail
      ? {
          organization_id: organizationId,
          invitee_email: detailInviteEmail,
          status: "pending",
        }
      : undefined,
  );
  const detailInvites = detailInvitesData?.items ?? groupedInvite?.invites ?? [];

  const memberWorkspaces = useMemo(() => {
    if (!orgMemberForDisplay || !workspacesData?.items) {
      return [];
    }
    const memberWorkspaceNames = new Set(
      orgMemberForDisplay.workspaces.map((ws: { name: string }) => ws.name),
    );
    return workspacesData.items.filter((w: Workspace) => memberWorkspaceNames.has(w.name));
  }, [orgMemberForDisplay, workspacesData?.items]);

  const memberForManageRoles = useMemo(() => {
    if (!orgMember || !selectedWorkspace || !workspaceMembersData?.members) {
      return null;
    }
    const workspaceMember = workspaceMembersData.members.find(
      (m: WorkspaceMember) => m.user_id === orgMember.user.id,
    );
    if (!workspaceMember?.id) {
      return null;
    }
    return {
      user_id: workspaceMember.id,
      name: orgMember.user.email,
      email: orgMember.user.email,
      id: `wm-${workspaceMember.id}`,
    };
  }, [orgMember, selectedWorkspace, workspaceMembersData?.members]);

  const isViewingSelf = orgMember && currentUser && orgMember.user.id === currentUser.id;
  const isShortHeight = useIsShortHeight(600);
  const isNarrowWidth = useIsNarrowWidth(1500);
  const isNarrowViewport = isShortHeight || isNarrowWidth;

  const handleWorkspaceRemoved = useCallback((workspace: Workspace) => {
    setRemovedWorkspaceNames(prev => new Set([...prev, workspace.name]));
  }, []);

  const handleOrganizationRoleUpdated = useCallback((newRole: OrganizationRole) => {
    setOrganizationRoleOverride(newRole);
  }, []);

  const {
    handleRemoveFromWorkspace,
    handleManageRoles,
    handleDeleteWithConfirm,
    handleRoleChange,
    userOwnerRoles,
    deleteButtonLabel,
    userInfoActions,
  } = useMemberDetailActions({
    member,
    orgMember: orgMemberForDisplay,
    groupedInvite,
    isOrgOwner: !!isOrgOwner,
    isViewingSelf: !!isViewingSelf,
    organizationId: organizationId ?? 0,
    organizationRoles,
    onOpenChange,
    setSelectedWorkspace,
    setIsManageRolesOpen,
    onMemberDelete,
    onInviteDelete,
    onWorkspaceRemoved: handleWorkspaceRemoved,
    onOrganizationRoleUpdated: handleOrganizationRoleUpdated,
  });

  const handleInviteDelete = () => {
    void refetchDetailInvites();
    onInviteDelete?.();
  };

  const handleInviteCreate = () => {
    void refetchDetailInvites();
    onInviteCreate?.();
  };

  const {
    workspaceTableData,
    workspacesColumns,
    workspacesActions,
    workspacesContextMenuItems,
    inviteToWorkspaceAction,
  } = useWorkspacesTable({
    orgMember: orgMemberForDisplay,
    memberWorkspaces,
    pendingInvites: detailInvites,
    isOrgOwner: !!isOrgOwner,
    isRBACEnabled: !!isRBACEnabled,
    isViewingSelf: !!isViewingSelf,
    isEnterpriseIdentityEnabled: !!organization?.is_enterprise_identity_enabled,
    handleRemoveFromWorkspace,
    handleManageRoles,
    isInviteDialogOpen,
    setIsInviteDialogOpen,
    organizationId,
    currentUserId: currentUser?.id,
    onInviteDelete: handleInviteDelete,
    onInviteCreate: handleInviteCreate,
  });

  const {
    inviteTableData,
    inviteTableColumns,
    inviteTableActions,
    inviteTableContextMenuItems,
    inviteToWorkspaceActionForInvitee,
  } = useInviteTable({
    inviteeEmail: groupedInvite?.invitee_email,
    invites: detailInvites,
    isOrgOwner: !!isOrgOwner,
    isInviteDialogOpen,
    setIsInviteDialogOpen,
    organizationId,
    onInviteDelete: handleInviteDelete,
    onInviteCreate: handleInviteCreate,
  });

  useEffect(() => {
    setSearch("");
    setPage(1);
    setRemovedWorkspaceNames(new Set());
    setOrganizationRoleOverride(null);
  }, [member?.id]);

  useEffect(() => {
    setPage(1);
  }, [debouncedSearch]);

  const memberDetailTabs = useMemo(
    () => [
      {
        id: 0,
        name: "User's Workspaces",
        outlineIcon: <BriefcaseIconOutline className="h-4 w-4" />,
        solidIcon: <BriefcaseIcon className="h-4 w-4" />,
        content: orgMember ? (
          <div className="flex-1 overflow-hidden">
            <DataTable<WorkspaceTableRow>
              data={workspaceTableData.filter(row =>
                row.workspace.name.toLowerCase().includes(search.toLowerCase()),
              )}
              columns={workspacesColumns as TableColumn<WorkspaceTableRow>[]}
              actions={workspacesActions}
              contextMenuItems={workspacesContextMenuItems}
              isLoading={isLoadingWorkspaces}
              emptyStateMessage="No workspaces"
              objectName="workspace"
              hasSearch
              search={search}
              setSearch={setSearch}
              searchPlaceholder="Search workspaces"
              fullHeight
              pagination={workspacesData?.pagination ?? null}
              page={page}
              perPage={perPage}
              onPageChange={setPage}
              onPageSizeChange={size => {
                setPerPage(size);
                setPage(1);
              }}
              pageSizeOptions={[10, 20, 50, 100]}
              action={inviteToWorkspaceAction}
            />
          </div>
        ) : (
          <div className="flex-1 overflow-hidden">
            <DataTable<InviteTableRow>
              data={inviteTableData.filter(invite =>
                invite.workspaceName.toLowerCase().includes(search.toLowerCase()),
              )}
              columns={inviteTableColumns}
              hasSearch
              search={search}
              setSearch={setSearch}
              searchPlaceholder="Search invites"
              fullHeight
              pagination={null}
              page={1}
              perPage={20}
              onPageChange={setPage}
              onPageSizeChange={size => {
                setPerPage(size);
                setPage(1);
              }}
              pageSizeOptions={[10, 20, 50, 100]}
              actions={inviteTableActions}
              contextMenuItems={inviteTableContextMenuItems}
              emptyStateMessage="No invites"
              objectName="invite"
              action={inviteToWorkspaceActionForInvitee}
            />
          </div>
        ),
      },
    ],
    [
      orgMember,
      workspaceTableData,
      workspacesColumns,
      workspacesActions,
      workspacesContextMenuItems,
      isLoadingWorkspaces,
      search,
      workspacesData?.pagination,
      page,
      perPage,
      inviteTableData,
      inviteTableColumns,
      inviteTableActions,
      inviteTableContextMenuItems,
      inviteToWorkspaceAction,
      inviteToWorkspaceActionForInvitee,
    ],
  );

  if (!member) {
    return null;
  }

  return (
    <>
      <Sheet open={open} onOpenChange={onOpenChange}>
        <SheetContent className="h-full w-full min-w-[45vw] gap-0 overflow-y-auto">
          <SheetHeader className="pb-0">
            <SheetTitle className="flex h-[36px] items-center gap-2 text-2xl font-semibold">
              <UserCircleIcon className="max-h-5 min-h-5 min-w-5 max-w-5 text-gray-400" />
              User Details
            </SheetTitle>
          </SheetHeader>

          <div className="flex h-full flex-col gap-1 px-4 pb-6 pt-4">
            <UserInfoSection
              member={member}
              orgMember={orgMemberForDisplay ?? orgMember}
              groupedInvite={groupedInvite}
              isOrgOwner={!!isOrgOwner}
              isViewingSelf={!!isViewingSelf}
              userOwnerRoles={userOwnerRoles}
              userInfoActions={userInfoActions}
              deleteButtonLabel={deleteButtonLabel}
              handleRoleChange={handleRoleChange}
              handleDeleteWithConfirm={handleDeleteWithConfirm}
              isNarrowViewport={isNarrowViewport}
            />

            {(orgMember || groupedInvite) && (
              <section className="flex h-full flex-col">
                <TabNavigation tabs={memberDetailTabs} selectedIndex={0} shouldBeSticky={false} />
              </section>
            )}
          </div>
        </SheetContent>
      </Sheet>

      {selectedWorkspace && (
        <ManageRolesDialog
          open={isManageRolesOpen}
          onOpenChange={isOpen => {
            setIsManageRolesOpen(isOpen);
            if (!isOpen) {
              setSelectedWorkspace(null);
            }
          }}
          member={memberForManageRoles as unknown as WorkspaceMemberData | null}
          workspaceId={selectedWorkspace.id}
          organizationId={organizationId}
        />
      )}
    </>
  );
};
